import { Button } from "@/components/ui/baseUi/button"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { infoToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { useBoxPrograms } from "@/modules/boxProgrammation/hooks/useBoxProgram"
import { Path } from "@/modules/navigation/routes"
import { PlusIcon } from "@images/svgIcons/plusIcon"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { BoxOfferCard } from "@modules/boxOffer/components/boxOfferCard"
import { BoxOfferLiteVersion, BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { StatusFilter } from "@modules/boxOffer/gateway/boxOfferDto"
import { useBoxOffers } from "@modules/boxOffer/hooks/useBoxOffer"
import { useBoxOfferSidebar } from "@modules/boxOffer/hooks/useBoxOfferSidebar"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { StripeAccountStatus } from "@modules/payment/stripeTypes"
import { ScreenHeader } from "@modules/ui/components/header"
import { isValueInEnum } from "@modules/utils/utils"
import { CircleQuestionMark, Search, SettingsIcon, X } from "lucide-react"
import { useCallback, useMemo, useState } from "react"

export function BoxOfferPage() {
  const { data: offers } = useBoxOffers()
  const { data: archivedOffers } = useBoxOffers({ statusFilter: StatusFilter.ARCHIVED })
  const { navigateToBoxOfferCards, navigateToBoxOfferResume, navigateToBoxOfferOrderForm, isSidebarVisible } =
    useBoxOfferSidebar()
  const boxNavigate = useBoxNavigate()
  const { result: box } = useCurrentBox()
  const { data: programs } = useBoxPrograms()
  const [filter, setFilter] = useState<BoxOfferType | "ALL" | "ARCHIVED">("ALL")
  const [researchFilter, setResearchFilter] = useState("")
  const canCreateOffer = useBoxStaffPermission(BoxStaffPermissions.CREATE_OFFER)
  const canEditOffersOrder = useBoxStaffPermission(BoxStaffPermissions.EDIT_OFFER)

  const isBoxOfferLiteVersion = useMemo(
    () =>
      box &&
      (box.stripeAccountStatus === StripeAccountStatus.REQUIRE_ADDITIONAL_INFORMATION ||
        box.stripeAccountStatus === StripeAccountStatus.ONBOARDING ||
        box.stripeAccountStatus === StripeAccountStatus.UNDEFINED)
        ? true
        : false,
    [box],
  )

  const filteredOffers = useMemo(() => {
    if (filter === "ARCHIVED") {
      return archivedOffers.filter(
        (offer) =>
          offer.name.toLowerCase().indexOf(researchFilter.toLowerCase()) >= 0 ||
          (offer.description && offer.description.toLowerCase().indexOf(researchFilter.toLowerCase()) >= 0),
      )
    }

    return offers
      .filter((offer) => (filter === "ALL" ? offer : offer.type === filter))
      .filter(
        (offer) =>
          offer.name.toLowerCase().indexOf(researchFilter.toLowerCase()) >= 0 ||
          (offer.description && offer.description.toLowerCase().indexOf(researchFilter.toLowerCase()) >= 0),
      )
  }, [archivedOffers, filter, offers, researchFilter])

  const onAdd = useCallback(() => {
    if (programs.length > 0)
      return isSidebarVisible
        ? undefined
        : navigateToBoxOfferCards(filter !== "ALL" && filter !== "ARCHIVED" ? filter : undefined)
    else
      return toast.info(word("box.offer.form.error.createProgramFirst"), {
        classNames: infoToastClassName(),
      })
  }, [isSidebarVisible, filter, programs, navigateToBoxOfferCards])

  const liteVersionInformation = () => {
    return (
      <Text className="ml-2 flex flex-row items-center text-red-500">
        - {word("offer_list.lite_version.title")}
        <Tooltip>
          <TooltipTrigger render={<CircleQuestionMark className="ml-4 text-red-500" strokeWidth={1} />} />

          <TooltipContent side="bottom" className="max-w-60 rounded-xl bg-neutral-200 p-4">
            <div className="text-justify">
              <Text fontStyle="BM">
                {word("offer_list.lite_version.tooltip")}
                <span
                  onClick={() => boxNavigate(Path.Box.Admin + "?tab=banking")}
                  className="cursor-pointer text-textDefault-body underline"
                >
                  {word("global.here")}
                </span>
              </Text>
            </div>
          </TooltipContent>
        </Tooltip>
      </Text>
    )
  }

  return (
    <div className="flex w-full flex-col p-6">
      <ScreenHeader
        title={word("menu.offers")}
        subTitle={word("offer.header.subtitle")}
        rightButton={isBoxOfferLiteVersion && liteVersionInformation()}
      />

      <div className="mb-5 flex flex-wrap gap-2">
        <Button variant={filter === "ALL" ? "primary" : "outline"} size="lg" onClick={() => setFilter("ALL")}>
          {word("global.filters.all")}
        </Button>
        {Object.values(BoxOfferType).map((type, index) => (
          <Button
            key={`${index}-${type}`}
            variant={filter === type ? "primary" : "outline"}
            size="lg"
            onClick={() => setFilter(type)}
            disabled={isBoxOfferLiteVersion && !isValueInEnum(type, BoxOfferLiteVersion) ? true : false}
          >
            {word(`box.offer.form.types.${type}`)}
          </Button>
        ))}
        <Button variant={filter === "ARCHIVED" ? "primary" : "outline"} size="lg" onClick={() => setFilter("ARCHIVED")}>
          {word("global.archived", { count: 2 })}
        </Button>

        <div className="mr-8 flex w-60 items-center gap-3 md:ml-auto">
          <InputGroup>
            <InputGroupInput
              placeholder={word("global.search.placeholder")}
              onChange={(e) => setResearchFilter(e.currentTarget.value)}
              value={researchFilter}
            />
            <InputGroupAddon align="inline-start">
              <Search />
            </InputGroupAddon>
            {researchFilter && (
              <InputGroupAddon align="inline-end">
                <X className="size-4 cursor-pointer" onClick={() => setResearchFilter("")} />
              </InputGroupAddon>
            )}
          </InputGroup>
          <Button
            variant="secondary"
            size="iconMd"
            onClick={() => {
              if (!canEditOffersOrder) return
              void navigateToBoxOfferOrderForm()
            }}
            disabled={!canEditOffersOrder}
          >
            <SettingsIcon className="size-4" />
          </Button>
        </div>
      </div>

      <section className="flex flex-wrap gap-3">
        {/* PERMISSIONS - Head-coach & Coach & Financial permissions denied */}
        {canCreateOffer && filter !== "ARCHIVED" && (
          <div className="flex min-h-40 w-full flex-col justify-between gap-4 rounded-xl border border-dashed border-primary-300 p-4 lg:w-[49%]">
            <Text fontStyle="TM">
              {filter === "ALL" ? word("box.offer.add.title") : word(`offer.form.${filter}.create`)}
            </Text>

            <Button onClick={() => void onAdd()} variant="primary" className="w-fit" size="lg">
              <PlusIcon color="currentColor" />
              {word("global.new")}
            </Button>
          </div>
        )}

        {filteredOffers.length > 0 &&
          filteredOffers.map((offer) => (
            <BoxOfferCard
              offer={offer}
              key={offer.id}
              onClick={() => void navigateToBoxOfferResume(offer.id)}
              className={`min-h-40 lg:w-[49%] ${offer.archived ? "border-none bg-neutral-50" : "border border-neutral-200"}`}
            />
          ))}
      </section>
    </div>
  )
}
